/**
 * Consumidor del Launch Handler API: honra el deep-link cuando la app ya estaba abierta
 * @module utils/launchHandler
 * @version 6.11.42.00
 *
 * Complementa el arreglo de v6.11.41.00 (ver `utils/swClientContext.ts`). El
 * Service Worker resuelve por su cuenta el caso normal —encuentra la ventana de
 * la PWA y la navega—, pero cuando esa ventana está **congelada** (Chrome
 * congela las pestañas y ventanas que llevan rato en segundo plano) no contesta
 * el ping y el SW cae a `clients.openWindow()`. Ahí ya no navega el SW: el
 * navegador **lanza la app**, y qué pasa con la URL lo decide `launch_handler`
 * del manifest.
 *
 * Con `client_mode: ["focus-existing", "auto"]` el navegador enfoca la ventana
 * que ya existe y deja la URL de destino en `window.launchQueue`. Sin un
 * consumidor, esa URL se descarta en silencio: el usuario toca la notificación,
 * la app aparece… y se queda en la pantalla donde estaba. Este módulo la lee y
 * navega.
 *
 * Por qué `focus-existing` y no `navigate-existing`: `navigate-existing` navega
 * la ventana existente en **todos** los lanzamientos, incluido tocar el ícono de
 * la pantalla de inicio. Eso recargaría la app y se llevaría puesto lo que el
 * jugador tenga a medio hacer (por ejemplo, cargando el resultado con la foto ya
 * elegida). Con `focus-existing` decidimos nosotros, y el criterio es: honrar el
 * deep-link, ignorar el lanzamiento "pelado".
 */

/** Subconjunto del Launch Handler API que usamos (todavía no está en lib.dom). */
interface LaunchParams {
  targetURL?: string;
}

interface LaunchQueue {
  setConsumer(consumer: (params: LaunchParams) => void): void;
}

const getLaunchQueue = (): LaunchQueue | undefined =>
  (window as unknown as { launchQueue?: LaunchQueue }).launchQueue;

/**
 * Decide si un lanzamiento pide ir a algún lado. Exportada para poder razonarla
 * (y probarla) sin montar el navegador.
 */
export const resolveLaunchTarget = (targetURL: string, current: URL): string | null => {
  let target: URL;
  try {
    target = new URL(targetURL, current.origin);
  } catch {
    return null;
  }

  // Otro origen: no es un lanzamiento nuestro.
  if (target.origin !== current.origin) return null;

  // Lanzamiento "pelado" = el `start_url` del manifest, o sea el ícono de la
  // pantalla de inicio. El usuario quiere volver a la app donde la dejó, no
  // reiniciarla: no se toca nada.
  if (target.pathname === '/' && !target.search) return null;

  // Ya estamos exactamente ahí.
  if (target.pathname === current.pathname && target.search === current.search) return null;

  return target.href;
};

/**
 * Registra el consumidor. No-op en navegadores sin Launch Handler API
 * (Chrome 110+; en el resto el lanzamiento sigue funcionando como siempre).
 */
export const initLaunchHandler = (): void => {
  const launchQueue = getLaunchQueue();
  if (!launchQueue?.setConsumer) return;

  launchQueue.setConsumer((params) => {
    if (!params?.targetURL) return;

    const href = resolveLaunchTarget(params.targetURL, new URL(window.location.href));
    if (!href) return;

    // Navegación real, no history.pushState: la app rutea por `pathname` al
    // arrancar (ver el efecto de deep-links en App.tsx), igual que cuando el SW
    // usa client.navigate().
    window.location.href = href;
  });
};
